/**
 * RequestShowing — client submits a property showing request.
 *
 * Address via Google Places autocomplete (no internal MLS lookup) + a preferred
 * date/time → POST /appointment/requests (status PENDING). The assigned agent is
 * emailed + texted; the client sees it under "My showing requests" on the home
 * dashboard and gets a confirmation once the agent confirms.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, Loader2, MapPin } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiClientError } from "@re-platform/api-client";
import {
  Button,
  PlacesAutocomplete,
  type SelectedPlace,
} from "@re-platform/ui";

import { useBraStatus } from "../trec/useBraStatus";
import { createAppointmentRequest } from "./appointmentApi";

import "./RequestShowing.css";

/** Mirrors appointment-svc's MAX_LEAD_DAYS. The server is the one that
 *  enforces it; this is so the client cannot pick a date it will refuse. */
const MAX_LEAD_DAYS = 31;

/** `datetime-local` wants a local wall-clock string, not an ISO instant —
 *  toISOString() would shift the bound by the browser's offset and let a
 *  client pick an hour the server then rejects. */
function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function RequestShowing() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [address, setAddress] = useState("");
  const [place, setPlace] = useState<SelectedPlace | null>(null);
  const [when, setWhen] = useState(""); // datetime-local value
  const [notes, setNotes] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      createAppointmentRequest({
        property_address: address.trim(),
        place_id: place?.placeId ?? null,
        latitude: place?.lat ?? null,
        longitude: place?.lng ?? null,
        // datetime-local is a local wall-clock string; new Date() reads it in the
        // browser's zone and toISOString() sends UTC, which the API stores as-is.
        starts_at: new Date(when).toISOString(),
        notes: notes.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-appointments"] });
    },
  });

  // The pickable range: from now to the booking horizon. Computed once per
  // render rather than memoised — it moves with the clock, and a stale `min`
  // would keep offering a minute that has already passed.
  const now = new Date();
  const minWhen = localInputValue(now);
  const maxWhen = localInputValue(
    new Date(now.getTime() + MAX_LEAD_DAYS * 24 * 60 * 60 * 1000),
  );

  const bra = useBraStatus();
  // NAR 2024: the Buyer Representation Agreement must be SIGNED before a showing.
  const blockedByBra = !bra.loading && !bra.signed;

  // `min`/`max` bound the picker, but the field is still typeable, so the same
  // rule is checked here — and again by the API, which is what actually
  // enforces it.
  const chosen = when ? new Date(when) : null;
  const outOfRange =
    chosen != null &&
    (chosen.getTime() <= now.getTime() ||
      chosen.getTime() > now.getTime() + MAX_LEAD_DAYS * 24 * 60 * 60 * 1000);

  const canSubmit =
    address.trim().length > 0 &&
    when.length > 0 &&
    !outOfRange &&
    !blockedByBra;

  if (mutation.isSuccess) {
    return (
      <div className="ch-page">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            padding: "48px 16px",
            textAlign: "center",
          }}
        >
          <Check size={40} color="var(--rp-color-success, #16a34a)" />
          <h1>Request sent</h1>
          <p style={{ color: "var(--rp-color-text-muted)", maxWidth: 420 }}>
            Your agent has been notified and will confirm a time with you. You
            can track it under <strong>My showing requests</strong>.
          </p>
          <Button onClick={() => navigate("/")}>Back to my dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ch-page">
      <header className="ch-head">
        <div>
          <h1>Request a showing</h1>
          <p>
            Enter a property address and a time that works for you — your agent
            is notified instantly.
          </p>
        </div>
      </header>

      <div className="rs-card">
        <div className="rs-field">
          <span className="rs-field__label">Property address</span>
          <div className="rs-control">
            <MapPin className="rs-control__icon" size={18} aria-hidden />
            <PlacesAutocomplete
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              className="rs-input rs-input--iconed"
              value={address}
              onChange={(text) => {
                setAddress(text);
                // A pick's place_id and coordinates describe the address as
                // picked; once the text is edited they would describe a
                // different one. The pick itself calls this with its own
                // address first, then onSelect sets the place again.
                setPlace(null);
              }}
              onSelect={(p: SelectedPlace) => setPlace(p)}
            />
          </div>
        </div>

        <label className="rs-field">
          <span className="rs-field__label">Preferred date &amp; time</span>
          <div className="rs-control">
            <CalendarClock className="rs-control__icon" size={18} aria-hidden />
            <input
              type="datetime-local"
              className="rs-input rs-input--iconed"
              value={when}
              min={minWhen}
              max={maxWhen}
              onChange={(e) => setWhen(e.target.value)}
            />
          </div>
          <span className="rs-field__hint">
            Any time from now up to {MAX_LEAD_DAYS} days ahead.
          </span>
        </label>

        <label className="rs-field">
          <span className="rs-field__label">Notes (optional)</span>
          <textarea
            className="rs-input rs-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Anything your agent should know…"
          />
        </label>

        {outOfRange && (
          <p className="rs-error" role="alert">
            {chosen != null && chosen.getTime() <= now.getTime()
              ? "That time has already passed — pick a future date."
              : `Showings can be requested up to ${MAX_LEAD_DAYS} days ahead.`}
          </p>
        )}

        {mutation.isError && (
          <div style={{ color: "var(--rp-color-danger, #dc2626)" }}>
            {mutation.error instanceof ApiClientError
              ? mutation.error.message
              : "Something went wrong. Please try again."}
          </div>
        )}

        {blockedByBra && (
          <div
            role="status"
            style={{
              background: "var(--rp-color-warning-faint, #fff7e6)",
              border: "1px solid var(--rp-color-warning, #b26b00)",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            {bra.formId ? (
              <>
                Please sign your <strong>Buyer Representation Agreement</strong>{" "}
                before requesting a showing.{" "}
                <Link to={`/trec/sign/${bra.formId}`}>Review &amp; sign →</Link>
              </>
            ) : (
              <>
                You'll be able to request showings once your{" "}
                <strong>Buyer Representation Agreement</strong> is signed — it's
                sent once your document checklist is complete.
              </>
            )}
          </div>
        )}

        {/* Full width and alone: this card asks for three things and then
            does one. A Cancel button beside it competed with the only action
            on the page — the shell nav is how you leave. */}
        <Button
          className="rs-submit"
          disabled={!canSubmit || mutation.isPending}
          title={
            blockedByBra
              ? "Sign your Buyer Representation Agreement first"
              : undefined
          }
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? (
            <>
              <Loader2 size={14} className="ch-spin" aria-hidden /> Sending…
            </>
          ) : (
            "Send request"
          )}
        </Button>
      </div>
    </div>
  );
}
